import { addDays, addMonths, daysInMonth, type LocalDate } from '../dates';
import type { PayeeLike } from './types';

/** Never walk a schedule further than this (about 40 years of twice-monthly dues). */
const MAX_STEPS = 1000;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 'YYYY-MM' of a day. */
export const monthOf = (d: LocalDate): string => d.slice(0, 7);

/** 'YYYY-MM' + n months. */
export function addMonthsToMonth(month: string, n: number): string {
  return addMonths(`${month}-01`, n).slice(0, 7);
}

/** Whole months from a to b ('YYYY-MM'); negative when b is before a. */
export function monthsBetween(a: string, b: string): number {
  const [ay, am] = a.split('-').map(Number) as [number, number];
  const [by, bm] = b.split('-').map(Number) as [number, number];
  return (by - ay) * 12 + (bm - am);
}

/** Day `day` of a month, clamped to its last day (31 = always the last day). */
export function dayInMonth(month: string, day: number): LocalDate {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${pad2(Math.min(Math.max(1, day), daysInMonth(y, m)))}`;
}

export type ScheduleFields = Pick<
  PayeeLike,
  'repeat' | 'everyN' | 'dueDays' | 'startDate' | 'endDate' | 'paymentCount'
>;

/**
 * Every scheduled due date of a payee from its start, in order, up to `until` (inclusive), honoring
 * paymentCount and endDate. Monthly dues fall on each day in dueDays (two days = twice a month,
 * e.g. an online lending app's 3rd and 18th); weekly ones on startDate's weekday; yearly ones on
 * startDate's month. 'none' has no schedule.
 */
export function occurrences(s: ScheduleFields, until: LocalDate): LocalDate[] {
  const out: LocalDate[] = [];
  const every = Math.max(1, s.everyN || 1);
  const limit = (d: LocalDate) =>
    d > until ||
    (s.endDate !== null && d > s.endDate) ||
    (s.paymentCount !== null && out.length >= s.paymentCount);
  if (s.repeat === 'none') return out;

  if (s.repeat === 'weekly') {
    for (let i = 0, d = s.startDate; i < MAX_STEPS; i += 1, d = addDays(d, 7 * every)) {
      if (limit(d)) break;
      out.push(d);
    }
    return out;
  }

  const days = [...new Set(s.dueDays.length > 0 ? s.dueDays : [Number(s.startDate.slice(8, 10))])]
    .filter((d) => Number.isInteger(d) && d >= 1 && d <= 31)
    .sort((a, b) => a - b);
  const stepMonths = s.repeat === 'yearly' ? 12 * every : every;
  const yearlyDays = s.repeat === 'yearly' ? days.slice(0, 1) : days;
  const startMonth = monthOf(s.startDate);
  for (let i = 0; i < MAX_STEPS; i += 1) {
    const month = addMonthsToMonth(startMonth, i * stepMonths);
    const dates = [...new Set(yearlyDays.map((d) => dayInMonth(month, d)))];
    for (const d of dates) {
      if (d < s.startDate) continue;
      if (limit(d)) return out;
      out.push(d);
    }
    if (`${month}-01` > until) break;
  }
  return out;
}

/** The last scheduled date (payoff for a fixed loan), or null when the schedule never ends. */
export function lastOccurrence(s: ScheduleFields): LocalDate | null {
  if (s.repeat === 'none' || (s.paymentCount === null && s.endDate === null)) return null;
  const all = occurrences(s, s.endDate ?? '9999-12-31');
  return all[all.length - 1] ?? null;
}
